import { HiddenElementsScope } from '../hiddenElementsScope';

import * as platformEvents from 'platformEvents';
import coreUtilsLib from 'coreUtilsLib';

import {
  clearHighlights,
  openInlinePopupIfFocused,
  locateComponentAndHighlight,
} from '../hiddenElementsStateManagementUtils';
import {
  reportHiddenElementsShowBI,
  reportHoverHiddenElementBI,
} from '../hiddenElementsBI';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import type { HiddenElement } from '../hiddenElementsTypes';
import { isGhostComponent } from './ghostComponents';

export const unhideComponent = async (
  scope: HiddenElementsScope,
  element: HiddenElement,
) => {
  const { editorAPI } = scope;

  const compRef = await addToStage(scope, element);

  editorAPI.selection.deselectComponents();
  clearHighlights(editorAPI);

  await scrollTo(editorAPI, compRef);

  editorAPI.selection.selectComponentByCompRef(compRef);
  openInlinePopupIfFocused(editorAPI);

  editorAPI.history.add('unhide mobile component');
  reportHiddenElementsShowBI(editorAPI, element);
};

export const previewComponentOnStage = async (
  scope: HiddenElementsScope,
  element: HiddenElement,
) => {
  const { editorAPI } = scope;

  reportHoverHiddenElementBI(element);

  editorAPI.selection.deselectComponents();

  const compRef = await addToStage(scope, element);

  locateComponentAndHighlight(editorAPI, compRef);
  await scrollTo(editorAPI, compRef);
};

export const stopPreviewComponentOnStage = (
  scope: HiddenElementsScope,
  element: HiddenElement,
) => {
  const { editorAPI } = scope;
  const { dsActions } = editorAPI;

  if (element.isInteraction) {
    applyHiddenTransformationForMobileRef(editorAPI, element.compId);
  } else {
    const compRef = editorAPI.components.get.byId(element.compId);
    if (!compRef) {
      console.warn(
        'No compRef when showing preview, probably some race condition happened. Please investigate',
      );
      return;
    }

    dsActions.mobile.hiddenComponents.hide(compRef);
  }

  clearHighlights(editorAPI);
};

const addToStage = async (
  scope: HiddenElementsScope,
  element: HiddenElement,
): Promise<CompRef | null> => {
  const { editorAPI } = scope;
  const { dsActions } = editorAPI;

  // if addToStage is already in progress, we'd like to wait
  // in order to avoid adding same component twice
  await editorAPI.waitForChangesAppliedAsync();

  let compRef = editorAPI.components.get.byId(element.compId);
  if (
    compRef &&
    !isHiddenTransformationApplied(editorAPI, element.compId) &&
    !isGhostComponent(editorAPI, compRef)
  ) {
    // already on stage
    return compRef;
  }

  if (element.isInteraction) {
    removeHiddenTransformationForMobileRef(editorAPI, element.compId);
  } else {
    dsActions.mobile.hiddenComponents.show(element.compId, element.pageId);
  }

  await editorAPI.waitForChangesAppliedAsync();

  compRef = editorAPI.components.get.byId(element.compId);
  if (!compRef || !editorAPI.components.is.exist(compRef)) {
    // TODO: and fedops and sentry
    throw new Error('Failed to add component on stage');
  }

  editorAPI.components.hooks.componentAddedToStage.fire({
    compRef,
    type: 'click',
    origin: 'unHide',
  });

  return compRef;
};

const scrollTo = (editorAPI: EditorAPI, compRef: CompRef): Promise<void> => {
  return new Promise((resolve, reject) => {
    const compLayout =
      editorAPI.components.layout.getRelativeToScreen_rect(compRef);

    const SCROLL_MARGIN = 50;
    const targetY = compLayout.y - SCROLL_MARGIN;

    const scrollDuration = coreUtilsLib.scrollUtils.calcScrollDuration(
      editorAPI.site.getScroll().y,
      targetY,
      true,
    );

    const rejectionTimeout = setTimeout(() => {
      reject(
        new Error('Scroll timeout reached, probably scroll never happened'),
      );
    }, 5000);

    editorAPI.scroll.animateScrollTo({ y: targetY }, scrollDuration, () => {
      clearTimeout(rejectionTimeout);
      resolve();
    });

    editorAPI.dsActions.platform.notifyAppsOnCustomEvent(
      platformEvents.factory.showMobileElement({ compRef }),
    );
  });
};

const isHiddenTransformationApplied = (
  editorAPI: EditorAPI,
  compId: string,
) => {
  const compRefWithVariant = resolveCompRefWithVariant(editorAPI, compId);
  const transformations =
    editorAPI.components.transformations.get(compRefWithVariant);

  return transformations?.hidden === true;
};

const applyHiddenTransformationForMobileRef = (
  editorAPI: EditorAPI,
  compId: string,
) => {
  const compRefWithVariant = resolveCompRefWithVariant(editorAPI, compId);
  editorAPI.components.transformations.update(compRefWithVariant, {
    hidden: true,
  });
};

const removeHiddenTransformationForMobileRef = (
  editorAPI: EditorAPI,
  compId: string,
) => {
  const compRefWithVariant = resolveCompRefWithVariant(editorAPI, compId);
  editorAPI.components.transformations.update(compRefWithVariant, {
    hidden: false,
  });
};

const resolveCompRefWithVariant = (
  editorAPI: EditorAPI,
  compId: string,
): CompRef => {
  const compRef = editorAPI.components.get.byId(compId);
  const mobileVariant = editorAPI.mobile.getMobileVariant();

  return editorAPI.components.variants.getPointer(compRef, [mobileVariant]);
};
